import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  Tabs,
  Tab,
  AppBar,
  Toolbar,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
  Chip,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Alert,
} from '@mui/material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MenuIcon from '@mui/icons-material/Menu';
import DescriptionIcon from '@mui/icons-material/Description';
import TimelineIcon from '@mui/icons-material/Timeline';
import BugReportIcon from '@mui/icons-material/BugReport';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import { useAuth } from '../contexts/AuthContext';
import { loadDocumentation } from '../services/documentationService';

const Documentation = () => {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [selectedTab, setSelectedTab] = useState(0);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [docContent, setDocContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check if user has access (super_admin or admin)
  const hasAccess = profile?.platform_role === 'super_admin' || 
                    profile?.tenant_role === 'clinic_admin' ||
                    profile?.platform_role === 'admin';

  // Define documents array outside component to avoid dependency issues
  const documents = [
    {
      id: 'summary',
      title: 'Project Progress Summary',
      subtitle: 'Executive Overview for Stakeholders',
      icon: <DescriptionIcon />,
      color: 'info',
    },
    {
      id: 'architecture',
      title: 'Platform Architecture',
      subtitle: 'Single Source of Truth (SSOT)',
      icon: <DescriptionIcon />,
      color: 'primary',
    },
    {
      id: 'roadmap',
      title: 'Implementation Roadmap',
      subtitle: '12-Week Development Plan',
      icon: <TimelineIcon />,
      color: 'success',
    },
    {
      id: 'issues',
      title: 'Production Issues Tracker',
      subtitle: 'Active Issues & Resolutions',
      icon: <BugReportIcon />,
      color: 'warning',
    },
    {
      id: 'pricing',
      title: 'Price Validation & Scope Agreement',
      subtitle: 'Formal Agreement & Market Rate Validation',
      icon: <AttachMoneyIcon />,
      color: 'success',
    },
  ];

  // Load documentation when tab changes
  useEffect(() => {
    const loadDoc = async () => {
      setLoading(true);
      setError(null);
      try {
        const selectedDoc = documents[selectedTab];
        const content = await loadDocumentation(selectedDoc.id);
        setDocContent(content);
      } catch (err) {
        setError(err.message);
        setDocContent(`# Error Loading Documentation\n\n${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    if (hasAccess) {
      loadDoc();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTab, hasAccess]); // documents is a constant array, doesn't need to be in deps

  useEffect(() => {
    if (!hasAccess) {
      navigate('/dashboard');
    }
  }, [hasAccess, navigate]);

  if (!hasAccess) {
    return null;
  }

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
    if (isMobile) {
      setMobileDrawerOpen(false);
    }
  };

  const selectedDoc = documents[selectedTab];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Header */}
      <AppBar position="sticky" elevation={0} sx={{ bgcolor: 'background.paper', borderBottom: 1, borderColor: 'divider' }}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={() => navigate('/dashboard')}
            sx={{ mr: 2, color: 'text.primary' }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 600, color: 'text.primary' }}>
            Project Documentation
          </Typography>
          {isMobile && (
            <IconButton
              color="inherit"
              onClick={() => setMobileDrawerOpen(true)}
              sx={{ color: 'text.primary' }}
            >
              <MenuIcon />
            </IconButton>
          )}
        </Toolbar>
      </AppBar>

      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* Desktop Sidebar / Mobile Drawer */}
        {isMobile ? (
          <Drawer
            anchor="left"
            open={mobileDrawerOpen}
            onClose={() => setMobileDrawerOpen(false)}
            PaperProps={{
              sx: { width: 280 }
            }}
          >
            <Box sx={{ p: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Documents
              </Typography>
              <List>
                {documents.map((doc, index) => (
                  <ListItem key={doc.id} disablePadding>
                    <ListItemButton
                      selected={selectedTab === index}
                      onClick={() => handleTabChange(null, index)}
                    >
                      <ListItemText
                        primary={doc.title}
                        secondary={doc.subtitle}
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Box>
          </Drawer>
        ) : (
          <Paper
            elevation={0}
            sx={{
              width: 280,
              borderRight: 1,
              borderColor: 'divider',
              p: 2,
              overflow: 'auto',
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Documents
            </Typography>
            <List>
              {documents.map((doc, index) => (
                <ListItem key={doc.id} disablePadding sx={{ mb: 1 }}>
                  <ListItemButton
                    selected={selectedTab === index}
                    onClick={() => handleTabChange(null, index)}
                    sx={{
                      borderRadius: 2,
                      '&.Mui-selected': {
                        bgcolor: `${doc.color}.light`,
                        color: `${doc.color}.main`,
                        '&:hover': {
                          bgcolor: `${doc.color}.light`,
                        },
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                      <Box sx={{ mr: 2, color: 'inherit' }}>
                        {doc.icon}
                      </Box>
                      <ListItemText
                        primary={doc.title}
                        secondary={doc.subtitle}
                      />
                    </Box>
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Paper>
        )}

        {/* Main Content */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {/* Mobile Tabs */}
          {isMobile && (
            <Paper elevation={0} sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs
                value={selectedTab}
                onChange={handleTabChange}
                variant="scrollable"
                scrollButtons="auto"
                sx={{ borderBottom: 1, borderColor: 'divider' }}
              >
                {documents.map((doc) => (
                  <Tab
                    key={doc.id}
                    label={doc.title}
                    icon={doc.icon}
                    iconPosition="start"
                    sx={{ minHeight: 64 }}
                  />
                ))}
              </Tabs>
            </Paper>
          )}

          {/* Document Content */}
          <Box sx={{ flex: 1, overflow: 'auto', p: { xs: 2, sm: 3, md: 4 } }}>
            <Container maxWidth="lg" sx={{ py: 2 }}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 3, md: 4 },
                  borderRadius: 2,
                  bgcolor: 'background.paper',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
                }}
              >
                {/* Document Header */}
                <Box sx={{ mb: 4, pb: 2, borderBottom: 1, borderColor: 'divider' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Chip
                      icon={selectedDoc.icon}
                      label={selectedDoc.title}
                      color={selectedDoc.color}
                      sx={{ mr: 2, fontWeight: 600 }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      Last Updated: {new Date().toLocaleDateString()}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {selectedDoc.subtitle}
                  </Typography>
                </Box>

                {/* Loading State */}
                {loading && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                    <CircularProgress />
                  </Box>
                )}

                {/* Error State */}
                {error && !loading && (
                  <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                  </Alert>
                )}

                {/* Markdown Content */}
                {!loading && (
                  <Box
                  sx={{
                    '& h1': {
                      fontSize: '2rem',
                      fontWeight: 700,
                      mt: 4,
                      mb: 2,
                      color: 'text.primary',
                      borderBottom: 2,
                      borderColor: 'primary.main',
                      pb: 1,
                    },
                    '& h2': {
                      fontSize: '1.5rem',
                      fontWeight: 600,
                      mt: 3,
                      mb: 1.5,
                      color: 'text.primary',
                    },
                    '& h3': {
                      fontSize: '1.25rem',
                      fontWeight: 600,
                      mt: 2,
                      mb: 1,
                      color: 'text.primary',
                    },
                    '& h4, & h5, & h6': {
                      fontSize: '1rem',
                      fontWeight: 600,
                      mt: 2,
                      mb: 1,
                      color: 'text.primary',
                    },
                    '& p': {
                      mb: 2,
                      lineHeight: 1.7,
                      color: 'text.secondary',
                    },
                    '& ul, & ol': {
                      mb: 2,
                      pl: 3,
                    },
                    '& li': {
                      mb: 1,
                      lineHeight: 1.7,
                    },
                    '& code': {
                      bgcolor: 'rgba(0, 0, 0, 0.05)',
                      px: 1,
                      py: 0.5,
                      borderRadius: 1,
                      fontFamily: 'monospace',
                      fontSize: '0.9em',
                    },
                    '& pre': {
                      bgcolor: 'rgba(0, 0, 0, 0.05)',
                      p: 2,
                      borderRadius: 2,
                      overflow: 'auto',
                      mb: 2,
                      '& code': {
                        bgcolor: 'transparent',
                        p: 0,
                      },
                    },
                    '& blockquote': {
                      borderLeft: 4,
                      borderColor: 'primary.main',
                      pl: 2,
                      ml: 0,
                      fontStyle: 'italic',
                      color: 'text.secondary',
                    },
                    '& table': {
                      width: '100%',
                      borderCollapse: 'collapse',
                      mb: 2,
                      '& th, & td': {
                        border: 1,
                        borderColor: 'divider',
                        p: 1.5,
                        textAlign: 'left',
                      },
                      '& th': {
                        bgcolor: 'primary.light',
                        fontWeight: 600,
                      },
                    },
                    '& a': {
                      color: 'primary.main',
                      textDecoration: 'none',
                      '&:hover': {
                        textDecoration: 'underline',
                      },
                    },
                    '& hr': {
                      my: 3,
                      borderColor: 'divider',
                    },
                  }}
                >
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    rehypePlugins={[rehypeRaw]}
                  >
                    {docContent}
                  </ReactMarkdown>
                </Box>
                )}
              </Paper>
            </Container>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Documentation;

